import { useEffect, useState } from "react";
import { Copy, Check, RefreshCw, Loader2, KeyRound, ChevronDown, ChevronUp, Smartphone } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { WIDGET_SCRIPTS } from "@/utils/widgetScripts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "tc_widget_key";

const STEPS = [
  "Install the free Scriptable app. In Scriptable tap + once per script below, paste, and name it the same as here.",
  "Long-press your home screen → + → Scriptable → add a Large widget. Long-press it → Edit Widget → pick the script.",
  "Repeat for each script, then stack them: drag one Large widget onto another to form a Smart Stack and swipe between them.",
  "Taps open the site in Safari (iOS can't launch a home-screen bookmark from a widget). iOS decides refresh timing; 5 min is requested.",
];

const ScriptCard = ({ s, host, widgetKey }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const code = widgetKey ? s.build(host, widgetKey) : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — open the script and copy manually");
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/5" data-testid={`widget-script-${s.id}`}>
      <div className="flex items-center gap-3 p-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white/90">{s.name}</p>
          <p className="text-xs text-white/40 truncate">{s.desc}</p>
        </div>
        <button onClick={copy} disabled={!widgetKey} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#8B5CF6] text-white disabled:opacity-40" data-testid={`widget-script-copy-${s.id}`}>
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Copied" : "Copy"}
        </button>
        <button onClick={() => setOpen((v) => !v)} className="p-1.5 text-white/40 hover:text-white/80" data-testid={`widget-script-toggle-${s.id}`}>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>
      {open && (
        <textarea readOnly value={code || "Add or generate a widget key above to fill in the script."} onFocus={(e) => e.target.select()} className="w-full h-48 text-[10px] font-mono rounded-b-xl p-3 bg-black/30 text-white/70 border-t border-white/10 outline-none" data-testid={`widget-script-text-${s.id}`} />
      )}
    </div>
  );
};

export const WidgetScriptsSection = ({ getAuthHeader }) => {
  const [status, setStatus] = useState(null);
  const [widgetKey, setWidgetKey] = useState(() => localStorage.getItem(LS_KEY) || "");
  const [busy, setBusy] = useState(false);
  const host = window.location.host;

  useEffect(() => {
    axios.get(`${API}/admin/glance/widget-key`, getAuthHeader()).then((r) => {
      setStatus(r.data);
      if (r.data?.api_key) setWidgetKey(r.data.api_key);
    }).catch(() => setStatus({ has_key: false }));
  }, [getAuthHeader]);

  useEffect(() => {
    if (widgetKey) localStorage.setItem(LS_KEY, widgetKey);
  }, [widgetKey]);

  const generate = async () => {
    if (status?.has_key && !window.confirm("Generate a new key? Widgets using the old key will stop updating until you re-paste the scripts.")) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/glance/widget-key`, {}, getAuthHeader());
      setWidgetKey(data.api_key);
      setStatus({ has_key: true, key_prefix: data.key_prefix, api_key: data.api_key });
      toast.success("New widget key ready — re-copy each script");
    } catch {
      toast.error("Couldn't create widget key");
    } finally {
      setBusy(false);
    }
  };

  const keyMatches = !status?.has_key || !widgetKey || widgetKey.startsWith(status.key_prefix || "");
  const [check, setCheck] = useState(null);

  useEffect(() => {
    setCheck(null);
    if (!widgetKey || widgetKey.length < 20) return;
    let live = true;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/widget/glance?key=${encodeURIComponent(widgetKey)}`);
        if (live) setCheck(r.ok ? "ok" : "bad");
      } catch {
        if (live) setCheck("err");
      }
    }, 400);
    return () => { live = false; clearTimeout(t); };
  }, [widgetKey]);

  return (
    <div className="space-y-5" data-testid="widget-scripts-section">
      <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-[#8B5CF6]" />
          <p className="text-sm font-semibold text-white/90">How to set up</p>
        </div>
        <ol className="space-y-1.5">
          {STEPS.map((s, i) => (
            <li key={i} className="flex gap-2 text-xs text-white/60">
              <span className="w-4 h-4 rounded-full bg-[#8B5CF6]/20 text-[#8B5CF6] text-[10px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-[#00D4FF]" />
          <p className="text-sm font-semibold text-white/90">Widget key</p>
          <span className="text-xs text-white/40 ml-auto" data-testid="widget-key-status">
            {status?.has_key ? `Active · ${status.key_prefix}…` : status ? "No key yet" : "Loading…"}
          </span>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            value={widgetKey}
            onChange={(e) => setWidgetKey(e.target.value.trim())}
            placeholder="Paste your existing wdg_… key, or generate one"
            className="flex-1 px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-xs font-mono text-white/80 outline-none focus:border-[#8B5CF6]"
            data-testid="widget-key-input"
          />
          <button onClick={generate} disabled={busy} className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-white/10 hover:bg-white/15 text-white disabled:opacity-60" data-testid="widget-generate-key-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {status?.has_key ? "New key" : "Generate key"}
          </button>
        </div>
        {!keyMatches && (
          <p className="text-[11px] text-amber-400" data-testid="widget-key-mismatch">This key doesn't match the active key ({status.key_prefix}…). Scripts built with it won't load data.</p>
        )}
        {check === "ok" && <p className="text-[11px] text-emerald-400" data-testid="widget-key-check-ok">✓ Key accepted by the server — scripts copied below will load data.</p>}
        {check === "bad" && <p className="text-[11px] text-red-400" data-testid="widget-key-check-bad">✗ Server rejects this key. Tap "New key", then re-copy every script into Scriptable.</p>}
        {check === "err" && <p className="text-[11px] text-white/40" data-testid="widget-key-check-err">Couldn't reach the server to check the key.</p>}
        <p className="text-[11px] text-white/40">The key is filled into every script below. It's read-only and only shows what's in these widgets.</p>
      </div>

      <div className="space-y-2">
        {WIDGET_SCRIPTS.map((s) => <ScriptCard key={s.id} s={s} host={host} widgetKey={widgetKey} />)}
      </div>
    </div>
  );
};
